import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Lock, ShoppingCart, KeyRound } from "lucide-react";
import PortalSelfService from "@/components/PortalSelfService";
import PortalRequestsList from "@/components/PortalRequestsList";
import PortalOrdersList from "@/components/PortalOrdersList";
import PortalDriverTracker from "@/components/PortalDriverTracker";
import { logError } from "@/lib/log";

const ACCENT = "https://images.unsplash.com/photo-1521453204431-f4d478004ce1?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function CustomerPortal() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [meCust, setMeCust] = useState(null);
  const [poolsMissingGate, setPoolsMissingGate] = useState([]);
  const requestsRef = useRef(null);

  const loadInvoices = useCallback(async () => {
    try {
      const r = await api.get("/invoices");
      setInvoices(r.data);
    } catch (e) { logError("Portal invoice load failed:", e); }
  }, []);
  const loadMe = useCallback(async () => {
    if (!user?.customer_id) return;
    try {
      const r = await api.get("/portal/me");
      setMeCust(r.data.customer || null);
      // Pools with no entry instructions on file — flag so the driver isn't
      // stuck guessing a gate code on arrival.
      const pools = r.data.pools || [];
      setPoolsMissingGate(pools.filter((p) => !((p.entry_instructions || "").trim())));
    } catch (e) { logError("Portal customer load failed:", e); }
  }, [user?.customer_id]);
  useEffect(() => { loadInvoices(); loadMe(); }, [loadInvoices, loadMe]);

  const unpaid = invoices.filter((i) => i.status === "unpaid");
  const balance = unpaid.reduce((s, i) => s + Number(i.total || 0), 0);

  return (
    <div className="space-y-12 animate-fade-up">
      <div>
        <p className="overline">Welcome</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-1">{user?.name || "Customer"}</h1>
      </div>

      {meCust?.delivery_locked && (
        <div className="border border-destructive bg-destructive/5 rounded-sm p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="portal-lock-banner">
          <Lock className="w-5 h-5 text-destructive shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Deliveries are locked</p>
            <p className="text-sm text-muted-foreground">Please pay your open invoices to unlock delivery requests, or call the office.</p>
          </div>
          {unpaid[0] && (
            <Link to={`/portal/invoices/${unpaid[0].id}`}
                  className="inline-flex items-center justify-center bg-destructive text-white px-5 h-10 rounded-sm font-semibold hover:bg-destructive/90 transition-colors"
                  data-testid="portal-lock-pay-now">
              Pay now <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          )}
        </div>
      )}

      {poolsMissingGate.length > 0 && (
        <div className="border border-amber-500 bg-amber-50 dark:bg-amber-950/30 rounded-sm p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start gap-3" data-testid="portal-missing-gate-banner">
          <KeyRound className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900 dark:text-amber-200">
              {poolsMissingGate.length === 1 ? "Missing gate code / entry info" : `${poolsMissingGate.length} pools missing gate codes`}
            </p>
            <p className="text-sm text-amber-800/80 dark:text-amber-300/80 mt-1">
              Help our driver find your pool: add a gate code, lockbox combo, or any access instructions.
              {poolsMissingGate.length <= 3 && (
                <> Missing on: <span className="font-mono">{poolsMissingGate.map((p) => p.name).join(", ")}</span></>
              )}
            </p>
          </div>
          <a href="#portal-pools-anchor"
             onClick={(e) => {
               e.preventDefault();
               document.querySelector('[data-testid="portal-pools-card"]')
                 ?.scrollIntoView({ behavior: "smooth", block: "start" });
             }}
             className="inline-flex items-center justify-center bg-amber-600 hover:bg-amber-700 text-white px-4 h-9 rounded-sm font-semibold transition-colors shrink-0"
             data-testid="portal-missing-gate-cta">
            Add now <ArrowRight className="ml-2 w-4 h-4" />
          </a>
        </div>
      )}

      {/* Balance hero */}
      <div className="relative overflow-hidden border border-border rounded-sm shadow-sm">
        <img src={ACCENT} className="absolute inset-0 w-full h-full object-cover opacity-[0.06]" alt="" />
        <div className="relative p-6 sm:p-8 md:p-12 grid md:grid-cols-3 gap-6 md:gap-8">
          <div className="md:col-span-2">
            <p className="overline">Current balance</p>
            <div className="font-display text-5xl sm:text-6xl md:text-7xl font-black tracking-tighter mt-3 mono-num">{fmtMoney(balance)}</div>
            <p className="text-sm text-muted-foreground mt-3">{unpaid.length} open invoice{unpaid.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex md:flex-col md:items-end md:justify-end">
            {unpaid[0] ? (
              <Link to={`/portal/invoices/${unpaid[0].id}`}
                    className="inline-flex items-center justify-center bg-foreground text-background px-6 h-12 rounded-sm font-semibold hover:bg-foreground/90 transition-colors w-full md:w-auto"
                    data-testid="portal-pay-cta">
                Pay {unpaid[0].number} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            ) : (
              <div className="text-success font-bold uppercase tracking-wider text-sm">All paid up</div>
            )}
          </div>
        </div>
      </div>

      {user?.customer_id && (
        <>
          <PortalDriverTracker />
          <PortalSelfService onChanged={() => { requestsRef.current?.reload(); loadMe(); }} />
          <Link to="/portal/shop"
                className="flex items-center justify-between gap-4 border border-border rounded-sm px-6 py-5 hover:bg-secondary/40 transition-colors group"
                data-testid="portal-shop-link">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-5 h-5 text-primary" />
              <div>
                <div className="font-display text-xl font-bold tracking-tight">Shop products</div>
                <div className="text-sm text-muted-foreground">Pumps, heaters, chemicals &amp; more — delivered with your service.</div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
          </Link>
          <PortalRequestsList ref={requestsRef} />
          <PortalOrdersList />
        </>
      )}

      <section>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Your invoices</h2>
        <div className="border border-border rounded-sm overflow-hidden overflow-x-auto">
          <div className="min-w-[640px]">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-border overline">
            <div className="col-span-3">Number</div>
            <div className="col-span-3">Delivery</div>
            <div className="col-span-2">Gallons</div>
            <div className="col-span-2">Total</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {invoices.length === 0 && <div className="px-6 py-10 text-center text-sm text-muted-foreground">No invoices yet.</div>}
          {invoices.map((inv) => (
            <Link key={inv.id} to={`/portal/invoices/${inv.id}`}
                  className="grid grid-cols-12 px-6 py-4 border-b border-border last:border-b-0 hover:bg-secondary/40 transition-colors"
                  data-testid={`portal-invoice-${inv.number}`}>
              <div className="col-span-3 mono-num font-medium">{inv.number}</div>
              <div className="col-span-3 mono-num text-sm text-muted-foreground">{fmtDate(inv.delivery_date)}</div>
              <div className="col-span-2 mono-num text-sm">{inv.gallons}</div>
              <div className="col-span-2 mono-num font-semibold">{fmtMoney(inv.total)}</div>
              <div className="col-span-2 text-right">
                <span className={`text-xs font-bold uppercase tracking-wider ${inv.status === 'paid' ? 'text-success' : 'text-destructive'}`}>
                  {inv.status}
                </span>
              </div>
            </Link>
          ))}
          </div>
        </div>
      </section>
    </div>
  );
}
